import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useLanguage } from '../context/LanguageContext';
import { formatPrice } from '../utils/currency';
import { ordersAPI } from '../services/api';
import { toast } from 'react-toastify';
import SEO from '../components/SEO';

const Checkout = () => {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const { cart, refreshCart } = useCart();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    shipping_address: '',
    shipping_city: '',
    shipping_state: '',
    shipping_zip: '',
    shipping_country: 'USA',
    notes: '',
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await ordersAPI.create(formData);
      toast.success(t('toast.orderPlaced'), { autoClose: 1000 });
      await refreshCart();
      navigate(`/orders`);
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to place order';
      toast.error(message, { autoClose: 1000 });
    } finally {
      setLoading(false);
    }
  };

  if (cart.items.length === 0) {
    navigate('/cart');
    return null;
  }

  // Calculate cart total with proper pricing for tuning products
  const calculateCartTotal = () => {
    return cart.items.reduce((total, item) => {
      // Check if this is a tuning product (ECU tune)
      if (item.product.category === 'handheld_tuners') {
        // Use Stage 1 price (600 EUR = 2400 PLN)
        return total + (600 * item.quantity);
      }
      // Regular products use their actual price
      return total + (item.product.price * item.quantity);
    }, 0);
  };

  const cartTotal = calculateCartTotal();

  return (
    <>
      <SEO
        title="Checkout - Complete Your Order"
        description="Secure checkout for your motorcycle performance parts order. Fast shipping and multiple payment options available."
        keywords="checkout, secure payment, order completion"
        noindex={true}
      />

      <div className="container-custom py-12">
        <h1 className="text-3xl font-display font-bold mb-8">{t('checkout.title')}</h1>

        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="card p-6">
                <h2 className="text-xl font-bold mb-4">{t('checkout.contactInformation')}</h2>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('checkout.fullName')} *
                    </label>
                    <input
                      name="customer_name"
                      type="text"
                      required
                      value={formData.customer_name}
                      onChange={handleChange}
                      className="input-field"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('checkout.email')} *
                    </label>
                    <input
                      name="customer_email"
                      type="email"
                      required
                      value={formData.customer_email}
                      onChange={handleChange}
                      className="input-field"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('checkout.phone')}
                    </label>
                    <input
                      name="customer_phone"
                      type="tel"
                      value={formData.customer_phone}
                      onChange={handleChange}
                      className="input-field"
                    />
                  </div>
                </div>
              </div>

              <div className="card p-6">
                <h2 className="text-xl font-bold mb-4">{t('checkout.shippingAddress')}</h2>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('checkout.address')} *
                    </label>
                    <input
                      name="shipping_address"
                      type="text"
                      required
                      value={formData.shipping_address}
                      onChange={handleChange}
                      className="input-field"
                    />
                  </div>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('checkout.city')} *
                      </label>
                      <input
                        name="shipping_city"
                        type="text"
                        required
                        value={formData.shipping_city}
                        onChange={handleChange}
                        className="input-field"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('checkout.state')} *
                      </label>
                      <input
                        name="shipping_state"
                        type="text"
                        required
                        value={formData.shipping_state}
                        onChange={handleChange}
                        className="input-field"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {t('checkout.zipCode')} *
                      </label>
                      <input
                        name="shipping_zip"
                        type="text"
                        required
                        value={formData.shipping_zip}
                        onChange={handleChange}
                        className="input-field"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      {t('checkout.orderNotes')}
                    </label>
                    <textarea
                      name="notes"
                      rows="3"
                      value={formData.notes}
                      onChange={handleChange}
                      className="input-field"
                      placeholder={t('checkout.orderNotesPlaceholder')}
                    />
                  </div>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="btn btn-primary w-full"
              >
                {loading ? t('checkout.placingOrder') : t('checkout.placeOrder')}
              </button>
            </form>
          </div>

          <div className="lg:col-span-1">
            <div className="card p-6 sticky top-24">
              <h2 className="text-xl font-bold mb-4">{t('checkout.orderSummary')}</h2>
              <div className="space-y-3 mb-4">
                {cart.items.map((item) => {
                  const itemPrice = item.product.category === 'handheld_tuners' 
                    ? 600 
                    : item.product.price;
                  const itemTotal = itemPrice * item.quantity;
                  
                  return (
                    <div key={item.id} className="flex justify-between text-sm">
                      <span className="text-gray-600">
                        {item.product.name} x {item.quantity}
                      </span>
                      <span className="font-semibold">
                        {formatPrice(itemTotal, language)}
                      </span>
                    </div>
                  );
                })}
              </div>
              <div className="border-t pt-4">
                <div className="flex justify-between text-xl font-bold">
                  <span>{t('shoppingCart.total')}</span>
                  <span className="text-racing-red">{formatPrice(cartTotal * 4, language)}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Checkout;
